import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/formatters';

// The customer summary panel. Opened from the Top Customers grid on the
// Customer Data report and from the info icon beside a customer on the Break
// Spots sheet, so it lives here rather than inside either page.
//
// `customer` needs a `buyer_id`; the rest of the row it carries
// (customer_name, username, notes) paints the panel before the detail fetch
// lands. `onNotesUpdated` / `onUsernameUpdated` are optional — a caller with
// no grid row to refresh (the Break Spots sheet) omits them. `zIndex` lifts
// the panel over a caller's own modal (the Freebies surfaces sit at 2300-2400).

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-09" -> "Sep '26". The raw key is fine on a pill but too wide for six
// x-axis ticks in a 700px panel.
function shortMonth(month) {
  const [year, mon] = String(month || '').split('-');
  const label = MONTH_ABBR[parseInt(mon, 10) - 1];
  return label ? `${label} '${year.slice(2)}` : month;
}

const fmtDollarAxis = (v) => v >= 1000 ? `$${(v / 1000).toLocaleString()}k` : `$${v.toLocaleString()}`;

function StatTile({ label, value, size = 20 }) {
  return (
    <div className="card" style={{ padding: '12px', margin: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: `${size}px`, fontWeight: size >= 20 ? 700 : 600 }}>{value}</div>
    </div>
  );
}

export default function CustomerDetailModal({ customer, onClose, canEdit, onNotesUpdated, onUsernameUpdated, zIndex = 1000 }) {
  const [detail, setDetail] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [notesOverride, setNotesOverride] = useState(null);
  const username = detail?.buyer?.username ?? customer.username ?? null;
  const displayName = detail?.buyer?.customer_name || customer.customer_name || username || 'Unknown Customer';
  // `customer.notes` is what the caller's row holds (the Top Customers grid);
  // callers that open the panel without one fall back to the fetched buyer.
  // `notesOverride` keeps a just-saved value on screen for both.
  const notes = notesOverride ?? customer.notes ?? detail?.buyer?.notes ?? '';
  // This panel is a customer summary, so its tiles deliberately ignore the
  // page's date filter — `customer` carries range-scoped aggregates from the
  // Top Customers grid, `detail.lifetime` the buyer's whole history.
  const lifetime = detail?.lifetime ?? null;

  useEffect(() => {
    api.get(`/api/customers/${customer.buyer_id}/detail?limit=10`)
      .then(({ data }) => {
        setDetail(data);
        setOrders(data.orders);
        setTotalOrders(data.totalOrders);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [customer.buyer_id]);

  async function loadMore() {
    const { data } = await api.get(`/api/customers/${customer.buyer_id}/detail?limit=50&offset=0`);
    setOrders(data.orders);
    setShowAll(true);
  }

  async function saveNotes(notes) {
    await api.put(`/api/customers/${customer.buyer_id}/notes`, { notes });
    setEditingNotes(false);
    setNotesOverride(notes);
    if (onNotesUpdated) onNotesUpdated(customer.buyer_id, notes);
  }

  async function saveUsername(value) {
    const trimmed = (value || '').trim();
    const { data } = await api.put(`/api/customers/${customer.buyer_id}/username`, { username: trimmed });
    setEditingUsername(false);
    setDetail(prev => prev ? { ...prev, buyer: { ...prev.buyer, username: data.username } } : prev);
    if (onUsernameUpdated) onUsernameUpdated(customer.buyer_id, data.username);
  }

  const statusColor = (s) => {
    if (s === 'COMPLETED' || s === 'DELIVERED') return '#38a169';
    if (s === 'CANCELLED' || s === 'REFUNDED') return '#e53e3e';
    if (s === 'IN_TRANSIT' || s === 'AWAITING_SHIPMENT') return '#d69e2e';
    return 'var(--text-secondary)';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '5vh', overflowY: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: '100%', maxWidth: '700px', margin: '0 16px 40px', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>

        <h3 className="card-title" style={{ marginBottom: '16px' }}>{displayName}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <StatTile label="Total Spend" value={lifetime ? formatCurrency(lifetime.lifetime_value) : '--'} />
          <StatTile label="Spend (Last 30D)" value={detail ? formatCurrency(detail.last30?.total || 0) : '--'} />
          <StatTile label="Orders" value={lifetime ? lifetime.total_orders : '--'} />
          <StatTile label="Avg Order" value={lifetime ? formatCurrency(lifetime.avg_order_value) : '--'} />
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div style={{ flex: '0 1 158px' }}>
            <StatTile label="First Order" size={16}
              value={lifetime?.first_order_date ? formatDate(lifetime.first_order_date) : '--'} />
          </div>
          <div style={{ flex: '0 1 158px' }}>
            <StatTile label="Last Order" size={16}
              value={lifetime?.last_order_date ? formatDate(lifetime.last_order_date) : '--'} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', flexWrap: 'wrap', alignItems: 'center' }}>
          <div><strong>Buyer ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{customer.buyer_id}</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <strong>Username:</strong>
            {editingUsername ? (
              <>
                <input
                  autoFocus
                  value={usernameDraft}
                  onChange={e => setUsernameDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveUsername(usernameDraft);
                    else if (e.key === 'Escape') setEditingUsername(false);
                  }}
                  placeholder="@tiktok_handle"
                  style={{ fontSize: 13, padding: '2px 6px', width: 180 }}
                />
                <button className="btn btn-primary btn-sm" onClick={() => saveUsername(usernameDraft)}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingUsername(false)}>Cancel</button>
              </>
            ) : (
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: username ? 'var(--text)' : 'var(--text-light)' }}>
                {username || (canEdit ? 'Not matched' : '--')}
                {canEdit && (
                  <button
                    onClick={() => { setUsernameDraft(username || ''); setEditingUsername(true); }}
                    title="Edit username"
                    aria-label="Edit username"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, marginLeft: 4, color: 'var(--text-light)', verticalAlign: 'middle' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </button>
                )}
              </span>
            )}
          </div>
        </div>

        {detail?.statusBreakdown?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Order Status Breakdown</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {detail.statusBreakdown.map(s => (
                <span key={s.order_status} className="badge" style={{ background: statusColor(s.order_status) + '20', color: statusColor(s.order_status), border: `1px solid ${statusColor(s.order_status)}40` }}>
                  {(s.order_status || 'UNKNOWN').replace(/_/g, ' ')} ({s.count})
                </span>
              ))}
            </div>
          </div>
        )}

        {detail?.monthlySpend?.some(m => m.orders > 0) && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Monthly Spend (Last 6 Months)</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {detail.monthlySpend.map(m => (
                <div key={m.month} className="card" style={{ padding: '8px 12px', margin: 0, textAlign: 'center', minWidth: '90px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{m.month}</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(m.total)}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{m.orders} orders</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={detail.monthlySpend} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtDollarAxis} width={56} />
                  <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={shortMonth} />
                  <Line type="linear" dataKey="total" name="Spend" stroke="#3182ce" strokeWidth={2}
                        dot={{ r: 4, fill: '#3182ce', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Notes</div>
          {editingNotes ? (
            <div style={{ display: 'flex', gap: '6px' }}>
              <input id="detail-notes" defaultValue={notes} style={{ flex: 1 }} />
              <button className="btn btn-primary btn-sm" onClick={() => saveNotes(document.getElementById('detail-notes').value)}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingNotes(false)}>Cancel</button>
            </div>
          ) : (
            <div onClick={() => canEdit && setEditingNotes(true)}
              style={{ cursor: canEdit ? 'pointer' : undefined, color: notes ? 'var(--text)' : 'var(--text-light)', fontSize: '13px' }}>
              {notes || (canEdit ? 'Click to add notes...' : 'No notes')}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
            Recent Orders {totalOrders > 0 && `(${orders.length} of ${totalOrders})`}
          </div>
          {loading ? (
            <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>Loading...</p>
          ) : orders.length === 0 ? (
            <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No orders found</p>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order ID</th>
                      <th>Status</th>
                      <th>GMV</th>
                      <th>Fees</th>
                      <th>Settlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.order_id} onClick={() => window.open(`/orders/${o.order_id}`, '_blank')} style={{ cursor: 'pointer' }}>
                        <td>{formatDate(o.created_time)}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{o.order_id}</td>
                        <td><span style={{ color: statusColor(o.order_status), fontWeight: 500, fontSize: '12px' }}>{(o.order_status || '').replace(/_/g, ' ')}</span></td>
                        <td>{formatCurrency(o.gmv)}</td>
                        <td style={{ color: '#e53e3e' }}>{o.total_fees != null ? formatCurrency(-o.total_fees) : '--'}</td>
                        <td>{o.settlement_amount != null ? formatCurrency(o.settlement_amount) : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!showAll && totalOrders > orders.length && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} onClick={loadMore}>
                  Show all {totalOrders} orders
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
